import React from 'react';

function isRadioInputProps(props: InputGroupProps): props is RadioInputProps {
  return props.type === 'radio';
}

// Define the options interface for select inputs with id support
interface SelectOption {
  value: string;
  label: string;
  id?: string | number; // Optional id field for API payload
}

// Base props shared between input types
interface BaseInputGroupProps {
  label: string;
  name: string;
  className?: string;
  error?: string;
  placeholder?: string;
  disabled?: boolean; // Add disabled prop
}

// Props specific to text inputs
interface TextInputProps extends BaseInputGroupProps {
  type: 'text' | 'email' | 'password' | 'number' | 'tel' | 'date';
  value: string;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  inputVariant?: 'default' | 'floating';
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void; // Optional key press handler
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

// Props specific to textarea inputs
interface TextAreaProps extends BaseInputGroupProps {
  type: 'textarea';
  value: string;
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  rows?: number;
  inputVariant?: 'default' | 'floating';
}

// Props specific to file input
interface FileInputProps extends BaseInputGroupProps {
  type: 'filetype';
  value?: File | String |null;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  accept?: string;
}

// Props specific to input with add button
interface WithAddButtonProps extends BaseInputGroupProps {
  type: 'withaddbutton';
  value: string;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAdd: () => void;
  addButtonText?: string;
  inputVariant?: 'default' | 'floating';
}

// Props specific to select inputs with enhanced change handler
interface SelectInputProps extends BaseInputGroupProps {
  type: 'select';
  value: string | number;
  options: SelectOption[];
  handleChange: (e: React.ChangeEvent<HTMLSelectElement>, selectedOption?: SelectOption) => void;
  selectVariant?: 'default' | 'floating';
}

// Props specific to radio button group
interface RadioInputProps extends BaseInputGroupProps {
  type: 'radio';
  value: string;
  options: SelectOption[];
  handleChange: (e: React.ChangeEvent<HTMLInputElement>, selectedOption?: SelectOption) => void;
  radioVariant?: 'default' | 'inline';
}

// Combined props type with discriminated union
type InputGroupProps = TextInputProps | WithAddButtonProps | SelectInputProps | FileInputProps | TextAreaProps | RadioInputProps;

const InputGroup: React.FC<InputGroupProps> = (props) => {
  const { label, name, className = "", error } = props;
  // Add this fileInputRef for the file input
  const fileInputRef = React.useRef<HTMLInputElement>(null);


  // Enhanced change handler for select elements
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (props.type === 'select') {
      const selectedValue = e.target.value;
      const selectedOption = props.options.find(option => option.value === selectedValue);
      props.handleChange(e, selectedOption);
    }
  };

  // Enhanced change handler for radio elements
  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (props.type === 'radio') {
      const selectedValue = e.target.value;
      const selectedOption = props.options.find(option => option.value === selectedValue);
      props.handleChange(e, selectedOption);
    }
  };

  // Check if field has content (for floating label position)
  const hasContent = props.type === 'select'
    ? Boolean(props.value && props.value !== '' && props.value !== 'default')
    : props.type === 'filetype'
      ? Boolean(props.value)
      : Boolean(props.value && typeof props.value === 'string' && props.value.trim() !== '');

  // Render floating input
  if ((props.type !== 'select' && props.type !== 'withaddbutton' && props.type !== 'filetype' && 'inputVariant' in props && props.inputVariant === 'floating') ||
    (props.type === 'select' && props.selectVariant === 'floating') ||
    (props.type === 'withaddbutton' && props.inputVariant === 'floating')) {
    return (
      <div className={`relative mb-6 rounded-[50px] ${className}`}>
        {props.type === 'select' ? (
          <>
            <select
              id={name}
              name={name}
              value={props.value || 'default'}
              onChange={handleSelectChange}
              disabled={props.disabled}
              className={`
                peer w-full rounded-[50px] border border-gray-300 bg-white py-3 px-5 pt-3.5 pb-3.5 min-h-[48px] 
                text-gray-900 placeholder-transparent outline-none transition-all duration-200
                focus:border-blue-500 focus:ring-2 focus:ring-blue-200
                ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}
                ${hasContent ? 'pt-3.5 pb-3.5' : 'py-3'}
                ${(!props.value || props.value === 'default') ? 'text-gray-500' : 'text-gray-900'}
              `}
              
            >
              <option value="default" disabled hidden></option>
              {props.options.map((option) => (
                <option key={option.value} value={option.value} data-id={option.id} className="text-gray-900">
                  {option.label}
                </option>
              ))}
            </select>
            <label
              htmlFor={name}
              className={`
                absolute left-5 text-gray-500 transition-all duration-200 pointer-events-none
                ${hasContent || error ? 'top-0 -translate-y-1/2 text-xs font-medium bg-white px-2 ml-2 scale-85' : 'top-1/2 -translate-y-1/2'}
                peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-xs peer-focus:font-medium
                peer-focus:bg-white peer-focus:px-2 peer-focus:ml-2 peer-focus:scale-85
                ${error ? ' text-red-500 peer-focus:text-red-500' : 'peer-focus:text-blue-600'}
              `}
            >
              {label}
            </label>
            {error && <div className="mt-1 text-red-500 text-sm">{error}</div>}
          </>
        ) : isRadioInputProps(props) ? (
          (() => {
            const radioProps = props as RadioInputProps;
            return (
              <div className={`flex ${radioProps.radioVariant === 'inline' ? 'flex-row gap-4' : 'flex-col gap-2'}`}>
                {radioProps.options.map((option: SelectOption) => (
                  <label key={option.value} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name={name}
                      value={option.value}
                      checked={radioProps.value === option.value}
                      onChange={handleRadioChange}
                      disabled={radioProps.disabled}
                      className="accent-primary mr-2"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
                {error && <div className="mt-1 text-red-500 text-sm">{error}</div>}
              </div>
            );
          })()
        ) : props.type === 'withaddbutton' ? (
          <div className="relative flex">
            <div className="relative flex-grow">
              <input
                type="text"
                className={`
                  peer w-full rounded-l-[50px] border border-gray-300 bg-white py-3 px-5 pt-3.5 pb-3.5 min-h-[48px] 
                  text-gray-900 placeholder-transparent outline-none transition-all duration-200
                  focus:border-blue-500 focus:ring-2 focus:ring-blue-200
                  ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}
                  ${hasContent ? 'pt-6 pb-2' : 'py-3'}
                `}
                id={name}
                name={name}
                placeholder=" "
                value={props.value}
                onChange={props.handleChange}
                disabled={props.disabled}
                {...('onKeyPress' in props && typeof props.onKeyPress === 'function' ? { onKeyPress: props.onKeyPress as React.KeyboardEventHandler<HTMLInputElement> } : {})}
              />
              <label
                htmlFor={name}
                className={`
                  absolute left-5 text-gray-500 transition-all duration-200 pointer-events-none
                  ${hasContent ? 'top-0 -translate-y-1/2 text-xs font-medium bg-white px-2 ml-2 scale-85' : 'top-1/2 -translate-y-1/2'}
                  peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-xs peer-focus:font-medium
                  peer-focus:bg-white peer-focus:px-2 peer-focus:ml-2 peer-focus:scale-85
                  ${error ? 'peer-focus:text-red-500' : 'peer-focus:text-blue-600'}
                `}
              >
                {label}
              </label>
            </div>
            <button
              type="button"
              className="rounded-r-[50px] border border-gray-300 border-l-0 bg-white px-4 min-h-[48px] hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors"
              onClick={props.onAdd}
              disabled={props.disabled}
            >
              {props.addButtonText || 'Add'}
            </button>
            {error && <div className="mt-1 text-red-500 text-sm">{error}</div>}
          </div>
        ) : props.type === 'textarea' ? (
          <>
            <textarea
              className={`
                peer w-full rounded-2xl border border-gray-300 bg-white py-3 px-5 pt-6 pb-2 min-h-[120px] 
                text-gray-900 placeholder-transparent outline-none transition-all duration-200 resize-y
                focus:border-blue-500 focus:ring-2 focus:ring-blue-200
                ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}
              `}
              id={name}
              name={name}
              placeholder=" "
              value={props.value}
              onChange={props.handleChange}
              rows={props.rows || 4}
              disabled={props.disabled}
            />
            <label
              htmlFor={name}
              className={`
                absolute left-5 text-gray-500 transition-all duration-200 pointer-events-none
                ${hasContent ? 'top-0 -translate-y-1/2 text-xs font-medium bg-white px-2 ml-2 scale-85' : 'top-6'}
                peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-xs peer-focus:font-medium
                peer-focus:bg-white peer-focus:px-2 peer-focus:ml-2 peer-focus:scale-85
                ${error ? 'peer-focus:text-red-500' : 'peer-focus:text-blue-600'}
              `}
            >
              {label}
            </label>
            {error && <div className="mt-1 text-red-500 text-sm">{error}</div>}
          </>
        ) : (
          <>
            <input
              type={props.type}
              className={`
                peer w-full rounded-[50px] border border-gray-300 bg-white py-3 px-5 pt-3.5 pb-3.5 min-h-[48px] 
                text-gray-900 placeholder-transparent outline-none transition-all duration-200
                focus:border-blue-500 focus:ring-2 focus:ring-blue-200
                ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}
                ${(props.value && typeof props.value === 'string' && props.value.trim() !== '') || error ? 'pt-3.5 pb-3.5' : 'py-3'}
                ${props.disabled ? 'opacity-50 bg-gray-300 text-black-500 cursor-not-allowed' : ''}
              `}
              id={name}
              name={name}
              placeholder=" "
              value={props.value || ''}
              onChange={props.handleChange}
              disabled={props.disabled}
              {...('onKeyPress' in props && typeof props.onKeyPress === 'function' ? { onKeyPress: props.onKeyPress as React.KeyboardEventHandler<HTMLInputElement> } : {})}
              {...('onFocus' in props && typeof props.onFocus === 'function' ? { onFocus: props.onFocus as React.FocusEventHandler<HTMLInputElement> } : {})}
              {...('onBlur' in props && typeof props.onBlur === 'function' ? { onBlur: props.onBlur as React.FocusEventHandler<HTMLInputElement> } : {})}
            />
            <label
              htmlFor={name}
              className={`
                rounded-full absolute left-5 text-gray-500 transition-all duration-200 pointer-events-none
                ${(props.value && typeof props.value === 'string' && props.value.trim() !== '') || error ? 
                  'top-0 -translate-y-1/2 text-xs font-medium bg-white px-2 ml-2 scale-85' : 
                  'top-1/2 -translate-y-1/2'
                }
                peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-xs peer-focus:font-medium
                peer-focus:bg-white peer-focus:px-2 peer-focus:ml-2 peer-focus:scale-85
                ${error ? 'text-red-500 peer-focus:text-red-500' : 'peer-focus:text-blue-600'}
              `}
            >
              {label}
            </label>
            {error && <div className="mt-1 text-red-500 text-sm">{error}</div>}
          </>
        )}
      </div>
    );
  }

  // Render default style with Tailwind
  return (
    <div className={`mb-4 ${className}`}>
      {props.type !== 'filetype' && (
        <label htmlFor={name} className="mb-2.5 block font-medium text-black">
          {label}
        </label>
      )}
      <div className="relative">
        {props.type === 'filetype' ? (
          <div className="relative">
            {props.disabled ? (
              <div
                className="peer w-full text-sm text-gray-400 rounded-[50px] border border-gray-300 min-h-[48px] px-4 py-2 bg-gray-100 cursor-not-allowed flex items-center"
                style={{ cursor: 'not-allowed' }}
                tabIndex={0}
                aria-disabled="true"
              >
                <span>{label} (File upload disabled)</span>
              </div>
            ) : (
              <>
                <input
                  type="file"
                  id={name}
                  name={name}
                  onChange={props.handleChange}
                  accept={props.accept}
                  className="peer block w-full text-sm text-gray-900 file:mr-4 file:py-2 file:px-4
                         file:rounded-l-[50px] file:border-0 file:text-sm file:font-semibold
                         file:bg-gray-100 file:text-gray-700
                         hover:file:bg-red-500 hover:file:text-white
                         rounded-[50px] border border-gray-300 min-h-[48px] px-4 py-2
                         focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
                <label
                  htmlFor={name}
                  className="absolute left-5 -top-2 text-xs font-medium rounded bg-white px-2 
                            text-gray-500 peer-focus:text-blue-600"
                >
                  {label}
                </label>
              </>
            )}
          </div>
        ) : props.type === 'select' ? (
          <select
            id={name}
            name={name}
            value={props.value || 'default'}
            onChange={handleSelectChange}
            disabled={props.disabled}
            className={`w-full rounded-[50px] border bg-transparent py-3 px-4 min-h-[48px] outline-none appearance-none
              ${error
                ? 'border-red-500 focus:border-red-500'
                : 'border-gray-300 focus:border-blue-500 dark:border-gray-600 dark:focus:border-blue-500'
              }
              ${(!props.value || props.value === 'default') ? 'text-gray-500' : 'text-gray-900'}
            `}
          >
            <option value="default" disabled hidden></option>
            {props.options.map((option) => (
              <option key={option.value} value={option.value} data-id={option.id} className="text-gray-900">
                {option.label}
              </option>
            ))}
          </select>
        ) : props.type === 'withaddbutton' ? (
          <div className="relative flex">
            <input
              type="text"
              id={name}
              name={name}
              placeholder={props.placeholder || ""}
              value={props.value}
              onChange={props.handleChange}
              disabled={props.disabled}
              className={`w-full rounded-l-[50px] border bg-transparent py-3 px-4 min-h-[48px] outline-none
                ${error
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:border-blue-500 dark:border-gray-600 dark:focus:border-blue-500'
                }
              `}
            />
            <button
              type="button"
              onClick={props.onAdd}
              disabled={props.disabled}
              className="rounded-r-[50px] border border-l-0 border-gray-300 bg-white py-3 px-4 min-h-[48px] hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors"
            >
              {props.addButtonText || 'Add'}
            </button>
          </div>
        ) : props.type === 'textarea' ? (
          <textarea
            id={name}
            name={name}
            placeholder={props.placeholder || ""}
            value={props.value}
            onChange={props.handleChange}
            rows={props.rows || 4}
            disabled={props.disabled}
            className={`w-full rounded-2xl border bg-transparent py-3 px-4 min-h-[120px] outline-none resize-y
              ${error
                ? 'border-red-500 focus:border-red-500'
                : 'border-gray-300 focus:border-blue-500 dark:border-gray-600 dark:focus:border-blue-500'
              }
            `}
          />
        ) : (
          <input
            type={props.type}
            id={name}
            name={name}
            placeholder={props.placeholder || ""}
            value={props.value}
            onChange={props.handleChange}
            disabled={props.disabled}
            className={`w-full rounded-[50px] border bg-transparent py-3 px-4 min-h-[48px] outline-none
              ${error
                ? 'border-red-500 focus:border-red-500'
                : 'border-gray-300 focus:border-blue-500 dark:border-gray-600 dark:focus:border-blue-500'
              }
              ${props.disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}
            `}
          />
        )}
      </div>
      {error && (
        <div className="mt-1 text-red-500 text-sm">{error}</div>
      )}
    </div>
  );
};

export default InputGroup;